
import React, { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import awsmobile from '../aws-exports';
import { get } from 'aws-amplify/api';
import { Link } from 'react-router-dom';

import './DataList.css';

Amplify.configure(awsmobile);

// Banded on ACTIVE tickets — ones ClickUp recorded a change to in the last 7 days — not
// on how many sit in the "in progress" column. Nothing moves a ticket out of that column
// except someone remembering to, so the old count was queue depth: on 2026-08-07 only 4
// of 42 in-progress tickets had been touched in a week and the oldest had been idle 1337
// days, yet the person with the most abandoned tickets was labelled "Heavy".
// Still deliberately a different ramp from incident severity — "red = bad" from the
// ranking pages must not bleed into "number of open tasks".
function loadFor(active) {
    if (active > 5)  return { label: 'Heavy',  color: 'var(--sev-very-serious)', bg: 'var(--sev-very-serious-bg)' };
    if (active >= 4) return { label: 'Busy',   color: 'var(--sev-severe)',       bg: 'var(--sev-severe-bg)' };
    if (active >= 1) return { label: 'Steady', color: 'var(--accent)',           bg: 'var(--accent-subtle)' };
    return             { label: 'Idle',   color: 'var(--text-muted)',       bg: 'var(--sev-normal-bg)' };
}

function idleLabel(days) {
    if (days === null || days === undefined) return null;
    if (days < 1) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.round(days / 30)}mo ago`;
    return `${(days / 365).toFixed(1)}y ago`;
}

function MonitorList() {
    const [monitors, setMonitors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchMonitors();
    }, []);

    async function fetchMonitors() {
        try {
            const restOperation = await get({
                apiName: 'monitors',
                path: '/monitors'
            });

            const { body } = await restOperation.response; // Waiting for the promise to resolve
            const jsonResponse = await body.json();

            setMonitors(jsonResponse);
            setLoading(false);
        } catch (error) {
            setError('Error fetching monitors data');
            setLoading(false);
        }
    }

    const busiest = monitors.reduce((max, m) => Math.max(max, Number(m.active) || 0), 0);
    const totalStale = monitors.reduce((sum, m) => sum + (Number(m.stale) || 0), 0);
    const anomalies = monitors.filter((m) => m.anomaly);
    const wipLimit = monitors.find((m) => m.wip_limit)?.wip_limit ?? 2;

    const header = (
        <div className="page-header">
            <h2 className="page-title">Developer Activity</h2>
            {/* Deliberately says "changed", not "worked on". The signal is ClickUp's
                date_updated, which is a timestamp on the TICKET and carries no author — a
                due-date edit by a PM, someone else's comment or an automation all move it.
                Per-person attribution would need time entries, and this workspace does not
                use them: 0 entries in the last 7 days, 1 in 30, 15 in 90, all from one
                person. So this is the honest ceiling of what the data supports, and the
                wording must not promise more than that. */}
            <p className="page-subtitle">
                Tickets with a recorded change in ClickUp in the last 7 days, in any status,
                most active first. The change may be by anyone, not only the assignee.
                Parked counts tickets left in the in-progress column with no activity —
                queue backlog, not workload.
            </p>
        </div>
    );

    if (loading) return <>{header}<p className="loading-state" role="status">Loading workload…</p></>;
    if (error) return <>{header}<p className="error-state" role="alert">{error}</p></>;
    if (!monitors.length) {
        return <>{header}<p className="empty-state">No tasks are currently in progress.</p></>;
    }

    return (
        <div className="times-container">
            {header}
            {totalStale > 0 && (
                <p className="activity-note">
                    {totalStale} in-progress {totalStale === 1 ? 'ticket has' : 'tickets have'} had
                    no recorded activity for over 7 days. They are excluded from the load bars below.
                </p>
            )}

            {anomalies.length > 0 && (
                <p className="activity-note activity-note-anomaly">
                    <strong>{anomalies.length} over the WIP limit.</strong>{' '}
                    More than {wipLimit} tickets in progress at once is an anomaly on the board,
                    not capacity — usually work that finished without the ticket moving, or work
                    started and dropped: {anomalies.map((m) => m.username).join(', ')}.
                </p>
            )}

            <ul className="data-list">
                {monitors.map((monitor) => {
                    const active = Number(monitor.active) || 0;
                    const stale = Number(monitor.stale) || 0;
                    const hours = Number(monitor.hours_tracked) || 0;
                    const load = loadFor(active);
                    const lastTouch = idleLabel(monitor.idle_days);
                    return (
                        <li key={monitor.user_id}>
                            <Link to={`/monitors/${monitor.user_id}`} className="data-row">
                                <div className="data-row-top">
                                    <span className="data-name">{monitor.username}</span>
                                    {monitor.anomaly && (
                                        // Separate from the load band on purpose: the band says
                                        // how much is moving, this says the board is wrong. A
                                        // person can be Idle and anomalous at the same time —
                                        // that combination is the strongest signal on the page.
                                        <span className="data-tag data-tag-anomaly">
                                            {monitor.count} in progress
                                        </span>
                                    )}
                                    <span className="data-tag" style={{ color: load.color, background: load.bg }}>
                                        {load.label}
                                    </span>
                                    <span className="data-metric">
                                        {active} active
                                    </span>
                                </div>
                                <div className="data-bar-track">
                                    <div
                                        className="data-bar-fill"
                                        style={{
                                            width: busiest ? `${(active / busiest) * 100}%` : '0%',
                                            background: load.color,
                                        }}
                                    />
                                </div>
                                <div className="activity-meta">
                                    {lastTouch && <span>last touched {lastTouch}</span>}
                                    {/* Which columns the week's work sat in. With 16 statuses
                                        live, a bare "9 active" says nothing about whether the
                                        work moved forward or just accumulated comments. */}
                                    {Object.entries(monitor.statuses || {}).slice(0, 4).map(([s, n]) => (
                                        <span className="activity-status" key={s}>
                                            {s} <span className="activity-status-count">{n}</span>
                                        </span>
                                    ))}
                                    {/* The number the old page showed as workload. Kept visible,
                                        but named for what it is and never used for the bar. */}
                                    {stale > 0 && (
                                        <span className="activity-stale">
                                            {stale} parked
                                            {monitor.oldest_idle_days
                                                ? ` · oldest ${idleLabel(monitor.oldest_idle_days)}`
                                                : ''}
                                        </span>
                                    )}
                                    {hours > 0 && <span>{hours}h tracked</span>}
                                </div>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default MonitorList;
